/*  eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, TextInput } from '@gsa/afp-component-library';

import { useContractLine } from '../../provider/contract-line-provider';
import SIMakeModelPanel from '../../../contract-line/make-model/si-make-model-panel';
import { useSIMakeModelProvider } from '../../../contract-line/make-model/si-make-model-panel-provider';

import '../../../contract-line/make-model/si-make-model-panel.scss';

const SelectMakeModelModal = ({
  data,
  onClose,
  onSelect,
  showManufacturerNumber = false,
}) => {
  const {
    bodyMakeInputValue,
    bodyMake,
    bodyModelInputValue,
    bodyModel,
    chassisMakeInputValue,
    chassisMake,
    chassisModelInputValue,
    chassisModel,
    modelYear,
    dirtyFields,
    setDirtyFields,
    showChassis,
    requiresChassis,
  } = useSIMakeModelProvider();
  const makeCode = bodyMake?.value;
  const makeName = bodyMake?.label;
  const modelCode = bodyModel?.value;
  const modelName = bodyModel?.label;
  const chassisMakeCode = chassisMake?.value;
  const chassisMakeName = chassisMake?.label;
  const chassisModelCode = chassisModel?.value;
  const chassisModelName = chassisModel?.label;
  const { contractLine, lineTabData } = useContractLine();
  const [manufacturerModelNumber, setManufacturerModelNumber] = useState(
    lineTabData?.manufacturerModelNumber ||
      contractLine?.manufacturerModelNumber,
  );
  const [mMNIsDirty, setMMNIsDirty] = useState(false);
  const modalSubtitle = `${contractLine?.standardItem?.standardItemNumber} ${contractLine?.standardItem?.title}`;

  const onUpdateMakeModel = () => {
    // edit manufacturer model number
    if (showManufacturerNumber) {
      if (manufacturerModelNumber) {
        onSelect({ manufacturerModelNumber });
      } else {
        setMMNIsDirty(true);
      }
      return;
    }

    // edit make and model
    if (dirtyFields.length === 0) {
      onClose(); // no changes, just close the modal
      return;
    }

    const isMakeModelChange = !(
      dirtyFields.length === 1 && dirtyFields.includes('modelYear')
    );

    setDirtyFields([
      'bodyMake',
      'bodyModel',
      'chassisMake',
      'chassisModel',
      'modelYear',
    ]);

    const fields = [
      { missing: !makeCode && !bodyMakeInputValue, field: 'Body Make' },
      { missing: !modelCode && !bodyModelInputValue, field: 'Body Model' },
      {
        missing:
          showChassis &&
          requiresChassis &&
          !chassisMakeCode &&
          !chassisMakeInputValue,
        field: 'Chassis Make',
      },
      {
        missing:
          showChassis &&
          requiresChassis &&
          !chassisModelCode &&
          !chassisModelInputValue,
        field: 'Chassis Model',
      },
      { missing: !modelYear, field: 'Model year' },
    ];
    const missingField = fields.find((f) => f.missing);
    if (!missingField) {
      onSelect(
        {
          makeCode,
          modelCode,
          chassisMakeCode,
          chassisModelCode,
          make: {
            makeCode,
            makeName: makeName || bodyMakeInputValue,
            fleetStatus: bodyMake?.fleetApprovalStatus,
          },
          model: {
            modelCode,
            modelName: modelName || bodyModelInputValue,
            fleetStatus: bodyModel?.fleetApprovalStatus,
            bodyModelInputValue,
          },
          chassisMake: {
            makeCode: chassisMakeCode,
            makeName: chassisMakeName || chassisMakeInputValue,
            fleetStatus: chassisMake?.fleetApprovalStatus,
          },
          chassisModel: {
            modelCode: chassisModelCode,
            modelName: chassisModelName || chassisModelInputValue,
            fleetStatus: chassisModel?.fleetApprovalStatus,
          },
          modelYear,
        },
        isMakeModelChange,
      );
    }
  };

  return (
    <div className="afp-modal-wrapper">
      <div className="afp-modal-overlay">
        <Modal
          id="remove-standard-item-modal"
          variant="extra-large"
          title={<h2>{`Edit line item  ${data?.scheduleLine || ''}`}</h2>}
          onClose={onClose}
          actions={
            <div>
              <Button
                data-testid="bid-line-removal-modal-cancel-btn"
                variant="unstyled"
                onClick={onClose}
                label="Cancel"
              />
              <Button
                variant="primary"
                onClick={onUpdateMakeModel}
                label={
                  showManufacturerNumber
                    ? 'Select manufacturer model number'
                    : 'Select make and model and close'
                }
              />
            </div>
          }
        >
          <div
            className="text-body text-base-dark"
            aria-label={modalSubtitle}
            tabIndex="0"
          >
            <h4>{modalSubtitle}</h4>
          </div>
          {showManufacturerNumber ? (
            <div className="grid-container">
              <TextInput
                reqired
                label={
                  <span className="text-bold">Manufacturer model number</span>
                }
                value={manufacturerModelNumber}
                onChange={(evt) => {
                  setManufacturerModelNumber(evt.target.value);
                }}
                onBlur={() => {
                  setMMNIsDirty(true);
                }}
                errorMessage={
                  mMNIsDirty && !manufacturerModelNumber
                    ? 'This is a required field'
                    : undefined
                }
              />
            </div>
          ) : (
            <div className="make-model-options-panel margin-top-4 padding-top-4 padding-bottom-4">
              <SIMakeModelPanel />
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export const SelectMakeModelModalType = PropTypes.shape(
  PropTypes.objectOf({
    bidId: PropTypes.number.isRequired,
    id: PropTypes.string.isRequired,
    standardItem: PropTypes.objectOf({
      standardItemId: PropTypes.string.isRequired,
      standardItemNumber: PropTypes.string.isRequired,
      tags: PropTypes.shape({
        value: PropTypes.arrayOf(PropTypes.string),
      }),
    }),
    solicitationLine: PropTypes.objectOf({
      id: PropTypes.string.isRequired,
    }),
  }),
);

SelectMakeModelModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  data: SelectMakeModelModalType.isRequired,
  showManufacturerNumber: PropTypes.bool,
};

export default SelectMakeModelModal;
